import { IdeaDirectionType } from '@/enums/idea-list.enum.ts'
import { FillStrategies } from './action-idea-form.ts'

export const MAX_TRADE_CAP = 5_000_000

export function normalizeTradeReasonValue(value?: string | string[]): string {
  const reasonList = Array.isArray(value)
    ? value
    : (value ?? '').split(',').map((reason) => reason.trim())

  return reasonList
    .map((reason) => reason.trim())
    .filter(Boolean)
    .sort()
    .join(',')
}

export function clampToMaxTargetMarketValue(
  value: number,
  isGlobal: boolean,
  maxTargetMarketValue: number
): number {
  return isGlobal ? value : Math.min(value, maxTargetMarketValue)
}

export function isOverMaxTargetMarketValue(
  value: number,
  isGlobal: boolean,
  maxTargetMarketValue: number
): boolean {
  return !isGlobal && value > maxTargetMarketValue
}

export function calculateTargetPriceMultiplier(
  percentage: number,
  direction: IdeaDirectionType | ''
): number {
  return direction === IdeaDirectionType.Short ? 1 - percentage : 1 + percentage
}

export function calculateStopPriceMultiplier(
  percentage: number,
  direction: IdeaDirectionType | ''
): number {
  return direction === IdeaDirectionType.Short ? 1 + percentage : 1 - percentage
}

export function calculateTargetPrice(
  price: number,
  multiplier: number
): number {
  return parseFloat((price * multiplier).toFixed(2))
}

export function calculateStopPrice(price: number, multiplier: number): number {
  return parseFloat((price * multiplier).toFixed(2))
}

export interface EditModeMaxValueCapParams {
  ideaDetail: {
    targetMarketValue: number
    currentMarketValue: number
    direction: string
    ticker?: { maxUpSize?: string }
  }
  isShortableData?: { data: { isShortable: boolean } }
  fillStrategyData?: { data: { hasUnfilledAction: boolean } }
  isGlobal: boolean
}

export function calculateEditModeMaxTargetValue(
  params: EditModeMaxValueCapParams
): number {
  const { ideaDetail, isShortableData, fillStrategyData, isGlobal } = params

  // Global users always use buying power as max.
  if (isGlobal) {
    return MAX_TRADE_CAP
  }

  // Short on a non-shortable ticker: can't add more, cap at existing size.
  if (
    isShortableData &&
    fillStrategyData &&
    !isShortableData.data.isShortable &&
    ideaDetail.direction === IdeaDirectionType.Short
  ) {
    return fillStrategyData.data.hasUnfilledAction
      ? ideaDetail.targetMarketValue
      : ideaDetail.currentMarketValue
  }

  // Standard case: allow up to currentMarketValue + maxUpSize, capped at MAX_TRADE_CAP.
  const maxUpSize = ideaDetail.ticker?.maxUpSize
    ? parseFloat(ideaDetail.ticker.maxUpSize)
    : 0

  return Math.round(
    Math.max(
      Math.min(ideaDetail.currentMarketValue + maxUpSize, MAX_TRADE_CAP),
      ideaDetail.currentMarketValue
    )
  )
}

export function resolveInitialFillStrategy(
  fillStrategy: string | undefined
): string | undefined {
  return fillStrategy === '15min-VWAP' || fillStrategy === '1hour-VWAP'
    ? FillStrategies[0].value
    : fillStrategy
}

export interface HasEditedUpdateValuesParams {
  mode: 'create' | 'edit'
  ideaDetail:
    | {
        targetMarketValue: number
        target: number
        stop: number
        conviction: string | undefined
        horizon: string | undefined
        tradeReason?: string
        fillStrategy?: string
        auto_fill?: boolean
      }
    | undefined
  // Set by direct input, %BP buttons or Rebalance; a value comparison would
  // miss a re-send of the unchanged target after the position has drifted.
  hasTargetMarketValueEdited: boolean
  watchedTarget: number | undefined
  watchedStop: number | undefined
  watchedConviction: string | undefined
  watchedHorizon: string | undefined
  watchedTradeReason: string | string[] | undefined
  watchedFillStrategy: string | undefined
  watchedAutoFill: boolean | undefined
}

export interface EditedUpdateValues {
  hasEditedUpdateValues: boolean
  hasFillStrategyEdited: boolean
}

export function hasEditedUpdateValues(
  params: HasEditedUpdateValuesParams
): EditedUpdateValues {
  const {
    mode,
    ideaDetail,
    hasTargetMarketValueEdited,
    watchedTarget,
    watchedStop,
    watchedConviction,
    watchedHorizon,
    watchedTradeReason,
    watchedFillStrategy,
    watchedAutoFill,
  } = params

  if (mode !== 'edit' || !ideaDetail) {
    return { hasEditedUpdateValues: false, hasFillStrategyEdited: false }
  }

  const initialFillStrategy = resolveInitialFillStrategy(
    ideaDetail.fillStrategy
  )
  const initialTradeReason = normalizeTradeReasonValue(ideaDetail.tradeReason)

  const hasOtherFieldEdited =
    (watchedTarget ?? ideaDetail.target) !== ideaDetail.target ||
    (watchedStop ?? ideaDetail.stop) !== ideaDetail.stop ||
    (watchedConviction ?? ideaDetail.conviction) !== ideaDetail.conviction ||
    (watchedHorizon ?? ideaDetail.horizon) !== ideaDetail.horizon ||
    normalizeTradeReasonValue(watchedTradeReason ?? initialTradeReason) !==
      initialTradeReason ||
    (watchedAutoFill ?? ideaDetail.auto_fill) !== ideaDetail.auto_fill
  const hasFillStrategyEdited =
    (watchedFillStrategy ?? initialFillStrategy) !== initialFillStrategy

  return {
    hasEditedUpdateValues:
      hasTargetMarketValueEdited ||
      hasOtherFieldEdited ||
      hasFillStrategyEdited,
    hasFillStrategyEdited,
  }
}
